import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaBolt,
  FaBullhorn,
  FaCalendarCheck,
  FaChartLine,
  FaClipboard,
  FaPrint,
  FaTruck,
  FaEnvelope,
  FaMagic,
  FaStore,
  FaTimes,
} from 'react-icons/fa';
import type { ForfyCommerceContext } from '../ForfyCommerceCockpit';
import {
  FORFY_COMMERCE_AI_CONTRACT_VERSION,
  invokeForfyCommerceAi,
  type ForfyCommerceAiIntent,
  type ForfyCommerceAiResponse,
} from '../../../lib/forfyCommerceAiClient';
import ForfyShippingRatesPreview from './ForfyShippingRatesPreview';
import './ForfyMerchantPanel.css';

type ForfyMerchantPanelProps = {
  context?: ForfyCommerceContext | null;
  onClose: () => void;
  onOpenTab: (tab: string) => void;
  onSeedPrompt: (prompt: string) => void;
};

type ForfyPanelStep = 'propose' | 'review' | 'validate';

type ForfyTaskId =
  | 'offer'
  | 'improve'
  | 'marketing'
  | 'client'
  | 'operations'
  | 'performance';

type ForfyTask = {
  id: ForfyTaskId;
  label: string;
  helper: string;
  tab: string;
  intent: ForfyCommerceAiIntent;
  icon: React.ReactNode;
  placeholder: string;
};

type ForfyObjectSummary = {
  id: string;
  type: string;
  label: string;
  status?: string;
  helper?: string;
  source?: string;
  group?: string;
};

type ForfyObjectCollections = {
  offers: ForfyObjectSummary[];
  products: ForfyObjectSummary[];
  orders: ForfyObjectSummary[];
  reservations: ForfyObjectSummary[];
  messages: ForfyObjectSummary[];
  marketing: ForfyObjectSummary[];
  performance: ForfyObjectSummary[];
  profile: ForfyObjectSummary[];
};

type ForfyShippingLabelPrepDraft = {
  orderId: string;
  orderLabel: string;
  readinessLabel: string;
  requiredFields: string[];
  missingFields: string[];
  nextServerStep: string;
  mutationExecuted: false;
  realLabelGenerated: false;
};

const objectGroupsByTask: Record<ForfyTaskId, Array<keyof ForfyObjectCollections>> = {
  offer: ['offers', 'products'],
  improve: ['offers', 'products'],
  marketing: ['offers', 'products', 'marketing'],
  client: ['messages'],
  operations: ['orders', 'reservations'],
  performance: ['performance'],
};

const tasks: ForfyTask[] = [
  {
    id: 'offer',
    label: 'Créer une offre',
    helper: 'Titre, promesse, inclusions et conditions.',
    tab: 'offersHub',
    intent: 'compose_offer',
    icon: <FaStore />,
    placeholder: 'Ex. Week-end détente pour deux avec spa, souper local et départ tardif.',
  },
  {
    id: 'improve',
    label: 'Améliorer une offre',
    helper: 'Trois améliorations concrètes à valider.',
    tab: 'offersHub',
    intent: 'improve_offer',
    icon: <FaMagic />,
    placeholder: 'Colle ou résume une offre existante à améliorer.',
  },
  {
    id: 'marketing',
    label: 'Préparer marketing',
    helper: 'Posts, courriel, SMS et campagne.',
    tab: 'marketingHub',
    intent: 'build_marketing_pack',
    icon: <FaBullhorn />,
    placeholder: 'Ex. Promouvoir le forfait spa dimanche auprès des couples de Québec.',
  },
  {
    id: 'client',
    label: 'Répondre client',
    helper: 'Réponse claire, humaine et validée.',
    tab: 'messages',
    intent: 'build_customer_faq',
    icon: <FaEnvelope />,
    placeholder: 'Ex. Un client demande si le forfait est remboursable ou disponible samedi.',
  },
  {
    id: 'operations',
    label: 'Préparer opérations',
    helper: 'Tâches, confirmations, commandes et expédition.',
    tab: 'reserveHub',
    intent: 'build_follow_up_tasks',
    icon: <FaCalendarCheck />,
    placeholder: 'Ex. Trois réservations à confirmer et deux commandes à préparer.',
  },
  {
    id: 'performance',
    label: 'Analyser performance',
    helper: 'Comprendre les chiffres sans les modifier.',
    tab: 'performanceHub',
    intent: 'build_launch_plan',
    icon: <FaChartLine />,
    placeholder: 'Ex. L’offre reçoit des vues mais peu de conversions.',
  },
];

const isForfyTaskId = (value: unknown): value is ForfyTaskId =>
  tasks.some((task) => task.id === value);

const getForfyLaunchContext = (context?: ForfyCommerceContext | null) => {
  const merchantContext = (context?.merchantContext || {}) as Record<string, any>;
  const launch = merchantContext.forfyLaunch;

  return launch && typeof launch === 'object'
    ? launch as Record<string, any>
    : {};
};

const resolveInitialTaskId = (context?: ForfyCommerceContext | null): ForfyTaskId => {
  const launch = getForfyLaunchContext(context);

  return isForfyTaskId(launch.forfyTaskId) ? launch.forfyTaskId : 'offer';
};

const resolveInitialIdea = (context?: ForfyCommerceContext | null) => {
  const launch = getForfyLaunchContext(context);
  return String(launch.prompt || '').trim();
};

// COMMERCE_UX1D_OBJECT_PICKER_LOCK — choose a real dashboard object before asking Forfy IA.
const getForfyObjectCollections = (context?: ForfyCommerceContext | null): Partial<ForfyObjectCollections> => {
  const merchantContext = (context?.merchantContext || {}) as Record<string, any>;
  const objects = merchantContext.objects;

  return objects && typeof objects === 'object'
    ? objects as Partial<ForfyObjectCollections>
    : {};
};

const buildObjectChoices = (context: ForfyCommerceContext | null | undefined, taskId: ForfyTaskId) => {
  const collections = getForfyObjectCollections(context);
  const groups = objectGroupsByTask[taskId] || [];

  return groups.flatMap((group) => {
    const items = Array.isArray(collections[group]) ? collections[group] || [] : [];

    return items.map((item) => ({
      ...item,
      group,
    }));
  }).slice(0, 12);
};

// COMMERCE_UX1F_SHIPPO_LABEL_PREP_LOCK — shipping label preparation only, no real carrier purchase.
const isShippingOrderCandidate = (taskId: ForfyTaskId, selectedObject: ForfyObjectSummary | null) =>
  taskId === 'operations' && selectedObject?.type === 'order';

const buildShippingLabelPrepDraft = (selectedObject: ForfyObjectSummary | null): ForfyShippingLabelPrepDraft | null => {
  if (!selectedObject || selectedObject.type !== 'order') {
    return null;
  }

  return {
    orderId: selectedObject.id,
    orderLabel: selectedObject.label,
    readinessLabel: 'Préparation seulement',
    requiredFields: [
      'Adresse client complète',
      'Adresse commerce expéditeur',
      'Poids du colis',
      'Dimensions du colis',
      'Service de livraison choisi',
      'Validation humaine avant achat d’étiquette',
    ],
    missingFields: [
      'Confirmer adresse client',
      'Confirmer poids et dimensions',
      'Choisir un service de livraison',
      'Passer par une fonction serveur sécurisée',
    ],
    nextServerStep: 'Créer plus tard une fonction serveur sécurisée qui reçoit la commande validée, appelle le fournisseur d’expédition avec un secret serveur, puis retourne une étiquette imprimable et un numéro de suivi.',
    mutationExecuted: false,
    realLabelGenerated: false,
  };
};

const buildShippingLabelPrompt = (draft: ForfyShippingLabelPrepDraft) =>
  [
    `Prépare le flux d’expédition pour ${draft.orderLabel}.`,
    '',
    'Objectif : préparer une étiquette imprimable, sans générer d’étiquette réelle maintenant.',
    '',
    'À valider avant génération réelle :',
    ...draft.requiredFields.map((field) => `- ${field}`),
    '',
    'Important : la génération réelle doit passer par une fonction serveur sécurisée. Aucun secret dans le navigateur.',
  ].join('\n');

// COMMERCE_UX1C_FORFY_CONTEXTUAL_LAUNCH_LOCK
const resolveTargetTab = (context: ForfyCommerceContext | null | undefined, fallback: string) => {
  const launch = getForfyLaunchContext(context);
  return String(launch.targetTab || fallback);
};

const numberFrom = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const readPath = (source: Record<string, any>, path: string[]) =>
  path.reduce<any>((current, key) => (current && typeof current === 'object' ? current[key] : undefined), source);

const buildSnapshot = (context?: ForfyCommerceContext | null) => {
  const merchantContext = (context?.merchantContext || {}) as Record<string, any>;

  return {
    reservationsPending: numberFrom(readPath(merchantContext, ['reservations', 'pending'])),
    reservationsConfirmed: numberFrom(readPath(merchantContext, ['reservations', 'confirmed'])),
    ordersActionable: numberFrom(readPath(merchantContext, ['orders', 'actionable'])),
    unreadMessages: numberFrom(readPath(merchantContext, ['clients', 'unreadMessages'])),
    offers: numberFrom(readPath(merchantContext, ['catalog', 'offers'])),
    products: numberFrom(readPath(merchantContext, ['catalog', 'products'])),
    revenue: numberFrom(readPath(merchantContext, ['performance', 'revenue'])),
    profileCompletion: numberFrom(readPath(merchantContext, ['profile', 'completionPercent'])),
  };
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);

// COMMERCE_UX1E_FORFY_BROWSER_RESILIENCE_LOCK — never leave the merchant stuck on loading.
const FORFY_BROWSER_TIMEOUT_MS = 45000;

const formatForfyBrowserError = (error: unknown) => {
  const raw = error instanceof Error ? error.message : String(error || 'unknown_error');

  if (/403|forbidden|permission/i.test(raw)) {
    return 'Forfy IA a été bloqué par une permission serveur. Le dossier n’a pas été publié ni modifié. Réessaie après rafraîchissement ou vérifie la session marchand.';
  }

  if (/timeout|aborted|network|fetch/i.test(raw)) {
    return 'Forfy IA n’a pas répondu assez vite. Le bouton est libéré; tu peux réessayer sans perdre le contexte choisi.';
  }

  return 'Forfy IA n’a pas pu générer le dossier pour le moment. Rien n’a été publié ni modifié.';
};

const buildBlockedForfyResponse = (taskLabel: string, reason: string): ForfyCommerceAiResponse => ({
  contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
  status: 'blocked',
  title: `Forfy IA à relancer — ${taskLabel}`,
  category: taskLabel,
  customerPromise: reason,
  readinessScore: 0,
  readinessLabel: 'À vérifier',
  missingFields: [
    reason,
    'Réessaie la génération ou vérifie la session marchand.',
  ],
  offerSheet: reason,
  marketingPack: {
    facebookInstagram: '',
    tiktokReels: '',
    linkedin: '',
    email: '',
    smsMessenger: '',
  },
  launchPlan: [],
  followUpTasks: [],
  customerFaq: [],
  guardrails: [
    'Rien n’a été publié.',
    'Aucune donnée financière n’a été modifiée.',
    'Validation humaine obligatoire.',
  ],
});

const withForfyBrowserTimeout = async <T,>(promise: Promise<T>) =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('forfy_browser_timeout'));
    }, FORFY_BROWSER_TIMEOUT_MS);

    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });

const buildOutputText = (output: ForfyCommerceAiResponse | null, priceNote: string) => {
  if (!output) return 'Aucun dossier Forfy IA généré.';

  return [
    'DOSSIER FORFY IA',
    '',
    `Titre: ${output.title}`,
    `Statut: ${output.status}`,
    `Score: ${output.readinessScore}% — ${output.readinessLabel}`,
    '',
    'Promesse client',
    output.customerPromise,
    '',
    'Prix',
    priceNote.trim() || 'À confirmer par le commerçant. Le prix vous appartient.',
    '',
    'Fiche offre',
    output.offerSheet,
    '',
    'À valider',
    ...(output.missingFields.length ? output.missingFields.map((field) => `- ${field}`) : ['- Validation humaine finale']),
    '',
    'Marketing',
    `Facebook / Instagram: ${output.marketingPack.facebookInstagram}`,
    `TikTok / Reels: ${output.marketingPack.tiktokReels}`,
    `LinkedIn: ${output.marketingPack.linkedin}`,
    `Courriel: ${output.marketingPack.email}`,
    `SMS / Messenger: ${output.marketingPack.smsMessenger}`,
    '',
    'Tâches',
    ...output.followUpTasks.map((task, index) => `${index + 1}. ${task.title} — ${task.owner} — ${task.priority} — ${task.due}`),
    '',
    'Garde-fous',
    'Forfy prépare. Vous décidez.',
    'Rien n’est publié sans validation humaine.',
    'Giftshop reste l’autorité financière. Reserve reste l’autorité de disponibilité.',
  ].join('\n');
};

const ForfyMerchantPanel: React.FC<ForfyMerchantPanelProps> = ({
  context,
  onClose,
  onOpenTab,
  onSeedPrompt,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<ForfyTaskId>(() => resolveInitialTaskId(context));
  const [step, setStep] = useState<ForfyPanelStep>('propose');
  const [idea, setIdea] = useState(() => resolveInitialIdea(context));
  const [priceNote, setPriceNote] = useState('');
  const [output, setOutput] = useState<ForfyCommerceAiResponse | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'blocked'>('idle');
  const [copied, setCopied] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [shippingPrepDraft, setShippingPrepDraft] = useState<ForfyShippingLabelPrepDraft | null>(null);

  const selectedTask = tasks.find((task) => task.id === selectedTaskId) || tasks[0];
  const snapshot = useMemo(() => buildSnapshot(context), [context]);
  const launchContext = useMemo(() => getForfyLaunchContext(context), [context]);
  const objectChoices = useMemo(() => buildObjectChoices(context, selectedTaskId), [context, selectedTaskId]);
  const selectedObject = useMemo(
    () => objectChoices.find((item) => item.id === selectedObjectId) || objectChoices[0] || null,
    [objectChoices, selectedObjectId],
  );
  const showShippingLabelPrep = isShippingOrderCandidate(selectedTaskId, selectedObject);
  const outputText = useMemo(() => buildOutputText(output, priceNote), [output, priceNote]);

  useEffect(() => {
    const nextTaskId = resolveInitialTaskId(context);
    const nextIdea = resolveInitialIdea(context);

    setSelectedTaskId(nextTaskId);
    setStep('propose');
    setStatus('idle');
    setOutput(null);
    setSelectedObjectId('');
    setErrorMessage(null);
    setShippingPrepDraft(null);

    if (nextIdea) {
      setIdea(nextIdea);
    }
  }, [context]);

  const mainSuggestion = useMemo(() => {
    if (snapshot.ordersActionable > 0) return `Traiter ${snapshot.ordersActionable} commande${snapshot.ordersActionable > 1 ? 's' : ''} ou expédition.`;
    if (snapshot.reservationsPending > 0) return `Confirmer ${snapshot.reservationsPending} réservation${snapshot.reservationsPending > 1 ? 's' : ''}.`;
    if (snapshot.unreadMessages > 0) return `Répondre à ${snapshot.unreadMessages} message${snapshot.unreadMessages > 1 ? 's' : ''}.`;
    if (snapshot.offers === 0) return 'Créer votre première offre vendable.';

    return 'Choisir une action et laisser Forfy IA préparer le brouillon.';
  }, [snapshot]);

  const handleGenerate = async () => {
    const cleanIdea = idea.trim() || (selectedObject ? `Travaille sur ${selectedObject.label}` : '');

    if (!cleanIdea) {
      setErrorMessage('Décris l’action à préparer ou choisis un élément réel du dashboard.');
      setStatus('blocked');
      return;
    }

    setStatus('loading');
    setErrorMessage(null);
    setStep('propose');

    try {
      const result = await withForfyBrowserTimeout(invokeForfyCommerceAi({
        contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
        intent: selectedTask.intent,
        locale: 'fr-CA',
        merchantContext: {
          businessName: context?.businessName || null,
          businessType: selectedTask.label,
          region: 'Québec',
          plan: context?.businessPlan || null,
          dashboardSummary: snapshot,
          activeTab: context?.activeTab || null,
          activeNav: context?.activeNav || null,
          forfyLaunch: launchContext,
          targetTab: launchContext.targetTab || selectedTask.tab,
          selectedCategory: launchContext.selectedCategory || null,
          selectedTool: launchContext.selectedTool || null,
          selectedDashboardObject: selectedObject,
          objectPicker: {
            hasSelection: Boolean(selectedObject),
            choicesCount: objectChoices.length,
            readonly: true,
          },
          shippingLabelPrep: shippingPrepDraft
            ? {
                ...shippingPrepDraft,
                requiresServerFunction: true,
                browserSecretAccess: false,
              }
            : null,
        } as any,
        offerIdea: {
          title: cleanIdea,
          category: selectedTask.label,
          promise: cleanIdea,
          inclusions: [cleanIdea],
          limits: ['Validation humaine avant publication'],
          conditions: ['Prix à confirmer par le commerçant', 'Disponibilité à confirmer dans le module autorisé'],
          media: ['Photo principale à choisir', 'Visuel social à préparer'],
          selectedDashboardObject: selectedObject
            ? `${selectedObject.label} — ${selectedObject.status || 'statut à valider'}`
            : null,
          shippingLabelPrep: shippingPrepDraft
            ? 'Préparation expédition seulement. Étiquette réelle non générée.'
            : null,
        },
      }));

      setOutput(result);
      setStatus(result.status === 'blocked' ? 'blocked' : 'ready');

      if (result.status === 'blocked') {
        setErrorMessage(result.customerPromise || 'Forfy IA a retourné un dossier à vérifier.');
      }

      setStep('review');
    } catch (error) {
      const readableError = formatForfyBrowserError(error);
      setOutput(buildBlockedForfyResponse(selectedTask.label, readableError));
      setErrorMessage(readableError);
      setStatus('blocked');
      setStep('review');
    }
  };

  const handlePrepareShippingLabelDraft = () => {
    const draft = buildShippingLabelPrepDraft(selectedObject);

    if (!draft) {
      setErrorMessage('Choisis une commande réelle avant de préparer une expédition.');
      setStatus('blocked');
      return;
    }

    setShippingPrepDraft(draft);
    setIdea(buildShippingLabelPrompt(draft));
    setErrorMessage(null);
    setStatus('idle');
    setStep('propose');
  };

  const handleCopy = async () => {
    await navigator.clipboard?.writeText(outputText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const handlePrepareInOffers = () => {
    if (!output) return;

    const packet = {
      source: 'forfy-merchant-panel-ux1b',
      preparedAt: new Date().toISOString(),
      title: output.title,
      category: output.category,
      customerPromise: output.customerPromise,
      readinessScore: output.readinessScore,
      readinessLabel: output.readinessLabel,
      priceNote: priceNote.trim() || null,
      dossier: outputText,
    };

    try {
      window.localStorage.setItem('forfy-commerce-offer-handoff-draft', JSON.stringify(packet));
    } catch {
      // Le navigateur peut bloquer localStorage; le flux manuel reste disponible.
    }

    window.dispatchEvent(new CustomEvent('commerce:offer-draft-prepared', { detail: packet }));
    onOpenTab('offersHub');
    setStep('validate');
  };

  const handleSendToAssistant = () => {
    onSeedPrompt(outputText);
    setStep('validate');
  };

  return (
    <section className="forfy-merchant-panel" data-forfy-merchant-panel="ux1b" aria-label="Forfy IA">
      <header className="forfy-merchant-panel__header">
        <div>
          <p>Forfy IA</p>
          <h2>{context?.businessName || 'Votre commerce'}</h2>
          <span>Forfy prépare. Vous décidez.</span>
        </div>

        <button type="button" onClick={onClose} aria-label="Fermer Forfy IA">
          <FaTimes />
        </button>
      </header>

      <div className="forfy-merchant-panel__steps" aria-label="Étapes Forfy IA">
        {[
          ['propose', '1', 'Proposition'],
          ['review', '2', 'Ajuster'],
          ['validate', '3', 'Valider'],
        ].map(([id, number, label]) => (
          <button
            key={id}
            type="button"
            className={step === id ? 'is-active' : ''}
            onClick={() => setStep(id as ForfyPanelStep)}
          >
            <strong>{number}</strong>
            <span>{label}</span>
          </button>
        ))}
      </div>

      <main className="forfy-merchant-panel__body">
        <section className="forfy-merchant-panel__snapshot" data-forfy-merchant-context="connected">
          <article><strong>{snapshot.reservationsPending}</strong><span>Réservations à confirmer</span></article>
          <article><strong>{snapshot.ordersActionable}</strong><span>Commandes / expédition</span></article>
          <article><strong>{snapshot.unreadMessages}</strong><span>Messages clients</span></article>
          <article><strong>{formatMoney(snapshot.revenue)}</strong><span>Lecture performance</span></article>
        </section>

        <section className="forfy-merchant-panel__suggestion">
          <p><FaBolt /> Suggestion Forfy IA</p>
          <h3>{mainSuggestion}</h3>
          <span>Une seule prochaine action, reliée au commerce connecté.</span>
        </section>

        <section className="forfy-merchant-panel__task-list" aria-label="Actions Forfy IA">
          {tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              className={selectedTask.id === task.id ? 'is-active' : ''}
              onClick={() => {
                setSelectedTaskId(task.id);
                setStep('propose');
              }}
            >
              <span>{task.icon}</span>
              <div>
                <strong>{task.label}</strong>
                <small>{task.helper}</small>
              </div>
            </button>
          ))}
        </section>

        <section className="forfy-merchant-panel__workzone">
          {step === 'propose' && (
            <>
              <p className="forfy-merchant-panel__eyebrow">1 — Proposition</p>
              <h3>{selectedTask.label}</h3>

              {objectChoices.length > 0 && (
                <div className="forfy-merchant-panel__object-picker" data-forfy-real-object-picker="ux1d">
                  <label>Choisir un élément réel du dashboard</label>
                  <div>
                    {objectChoices.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={selectedObject?.id === item.id ? 'is-active' : ''}
                        onClick={() => setSelectedObjectId(item.id)}
                      >
                        <strong>{item.label}</strong>
                        <span>{item.status || 'À valider'}</span>
                        <small>{item.helper || 'Donnée existante du commerce'}</small>
                      </button>
                    ))}
                  </div>
                  <small>
                    {selectedObject
                      ? `Forfy IA travaillera sur : ${selectedObject.label}`
                      : 'Forfy IA peut aussi préparer une nouvelle action sans sélection.'}
                  </small>
                </div>
              )}

              {showShippingLabelPrep && (
                <div className="forfy-merchant-panel__shipping-prep" data-forfy-shippo-server-prep="ux1f">
                  <div>
                    <p><FaTruck /> Expédition</p>
                    <h4>Préparer l’étiquette d’impression</h4>
                    <span>
                      Forfy prépare les informations. La génération réelle devra passer par une fonction serveur sécurisée.
                    </span>
                  </div>

                  <ul>
                    <li>Adresse client complète</li>
                    <li>Adresse commerce expéditeur</li>
                    <li>Poids et dimensions du colis</li>
                    <li>Service de livraison à choisir</li>
                    <li>Validation humaine avant génération réelle</li>
                  </ul>

                  <ForfyShippingRatesPreview
                    selectedOrder={selectedObject}
                    businessName={context?.businessName || 'Commerce Forfeo'}
                  />

                  {/* UX1F legacy contract retained in source:
                      Préparer le brouillon d’étiquette
                      Étiquette réelle générée : non
                  */}

                  <button
                    type="button"
                    onClick={handlePrepareShippingLabelDraft}
                  >
                    <FaPrint /> Préparer les détails avec Forfy IA
                  </button>
                </div>
              )}

              <textarea
                value={idea}
                onChange={(event) => {
                  setIdea(event.currentTarget.value);

                  if (errorMessage) {
                    setErrorMessage(null);
                  }
                }}
                placeholder={selectedTask.placeholder}
                rows={5}
              />
              {status === 'blocked' && !idea.trim() && (
                <small className="forfy-merchant-panel__warning">
                  Décris l’action à préparer en une phrase.
                </small>
              )}
            </>
          )}

          {step === 'review' && (
            <>
              <p className="forfy-merchant-panel__eyebrow">2 — Ajuster</p>
              <h3>{output?.title || 'Dossier à ajuster'}</h3>

              {errorMessage && (
                <div className="forfy-merchant-panel__error" data-forfy-browser-error="ux1e">
                  <strong>Forfy IA n’a pas terminé correctement</strong>
                  <span>{errorMessage}</span>
                </div>
              )}

              <label>Promesse client</label>
              <textarea value={output?.customerPromise || ''} readOnly rows={4} />

              <label>Prix</label>
              <input
                value={priceNote}
                onChange={(event) => setPriceNote(event.currentTarget.value)}
                placeholder="À confirmer par le commerçant"
              />

              <small>Le prix vous appartient. Forfy IA ne fixe aucun prix officiel.</small>
            </>
          )}

          {step === 'validate' && (
            <>
              <p className="forfy-merchant-panel__eyebrow">3 — Valider</p>
              <h3>Prêt à transférer dans le bon module</h3>

              <div className="forfy-merchant-panel__result">
                <strong>{output?.title || selectedTask.label}</strong>
                <span>{output ? `${output.readinessScore}% — ${output.readinessLabel}` : 'À générer'}</span>
                <p>{output?.customerPromise || 'Générez un dossier avant de valider.'}</p>
              </div>
            </>
          )}
        </section>
      </main>

      <footer className="forfy-merchant-panel__footer">
        {step === 'propose' && (
          <button
            type="button"
            className="forfy-merchant-panel__primary"
            onClick={handleGenerate}
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Forfy IA prépare...' : 'Générer avec Forfy IA'}
          </button>
        )}

        {step === 'review' && (
          <>
            <button type="button" className="forfy-merchant-panel__secondary" onClick={handleCopy} disabled={!output}>
              <FaClipboard /> {copied ? 'Copié' : 'Copier'}
            </button>

            <button type="button" className="forfy-merchant-panel__primary" onClick={handlePrepareInOffers} disabled={!output}>
              Préparer dans Offres <FaArrowRight />
            </button>
          </>
        )}

        {step === 'validate' && (
          <>
            <button type="button" className="forfy-merchant-panel__secondary" onClick={handleSendToAssistant} disabled={!output}>
              Envoyer à Forfy
            </button>

            <button type="button" className="forfy-merchant-panel__primary" onClick={() => onOpenTab(resolveTargetTab(context, selectedTask.tab))}>
              Ouvrir le module <FaArrowRight />
            </button>
          </>
        )}

        <small>Rien n’est publié sans votre validation.</small>
      </footer>
    </section>
  );
};

export default ForfyMerchantPanel;
